import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useSearchParams } from "react-router-dom";

import { useMetricsBySession } from "@/shared/domain/metrics/hooks";
import { Button } from "@/shared/foundation/primitives";
import {
  fuzzyFilter,
  fuzzyMatchIndices,
  fuzzyMatchIndicesPartial,
  fuzzyScore,
} from "@/shared/text/fuzzyFilter";

import { useSessionOutletContext } from "../useSessionOutletContext";

import { MetricDetailCard } from "./MetricDetailCard";
import {
  ancestorPaths,
  buildMetricTree,
  findNodeByPath,
  latestRecord,
  type MetricTreeNode,
} from "./metricTree";
import styles from "./SessionMetricsTab.module.css";

const EM_DASH = "—";

/** Query param that drives (and is driven by) the selected-metric detail card. */
const SELECTED_METRIC_PARAM = "selectedMetric";

/**
 * Return a pruned copy of `tree` containing only nodes where `predicate`
 * returns true for the node itself or any descendant. Structure (intermediate
 * branches) is preserved so the tree remains navigable. `recordCount` is
 * recalculated to reflect only the surviving subtree.
 */
function filterTree(
  nodes: MetricTreeNode[],
  predicate: (node: MetricTreeNode) => boolean,
): MetricTreeNode[] {
  const result: MetricTreeNode[] = [];
  for (const node of nodes) {
    const filteredChildren = filterTree(node.children, predicate);
    if (predicate(node) || filteredChildren.length > 0) {
      const childCount = filteredChildren.reduce(
        (sum, c) => sum + c.recordCount,
        0,
      );
      result.push({
        ...node,
        children: filteredChildren,
        recordCount: node.records.length + childCount,
      });
    }
  }
  return result;
}

/**
 * "Metrics" tab on the session-detail surface. Lists every metric attached
 * to the session named in the URL (`/sessions/:sessionId/metrics`) as a
 * tree-shaped table: metric names are split on `.` and grouped under
 * collapsible parent rows so `imu.accel.x` and `imu.accel.y` share an
 * `imu.accel` branch.
 *
 * Tab is mounted only when the `release-metrics` feature flag is on.
 *
 * The toolbar provides three controls:
 *   1. A free-text **search** input that fuzzy-filters the tree.
 *   2. A **group** dropdown that narrows to a single top-level prefix.
 *   3. An **expand/collapse all** button.
 *
 * Tree state lives in this component (a `Set` of expanded paths) rather
 * than the URL — drill-state is ephemeral and not worth surviving a
 * navigation.
 *
 * Clicking a row that has records selects it and opens a `MetricDetailCard`
 * to the right of the table (sticky to the scroll position). The selection is
 * held as a path so it survives a tree rebuild on data refetch.
 */
export function SessionMetricsTab() {
  const { session } = useSessionOutletContext();
  const sessionId = session.session_id;
  const orgId = session.org_id;

  const { data, isLoading, error } = useMetricsBySession(sessionId, orgId);

  const tree = React.useMemo<MetricTreeNode[]>(
    () => (data ? buildMetricTree(data) : []),
    [data],
  );

  // -- Toolbar filter state --------------------------------------------------

  const [searchText, setSearchText] = React.useState("");
  const [groupPrefixes, setGroupPrefixes] = React.useState<string[]>([]);

  // Unique first-segment prefixes, sorted, for the group dropdown.
  const groupOptions = React.useMemo(
    () => tree.map((root) => root.label).sort((a, b) => a.localeCompare(b)),
    [tree],
  );

  // Apply group prefix filter, then fuzzy search filter.
  const filteredTree = React.useMemo(() => {
    let result = tree;

    if (groupPrefixes.length > 0) {
      const selected = new Set(groupPrefixes);
      result = result.filter((root) => selected.has(root.label));
    }

    if (searchText.trim() !== "") {
      result = filterTree(result, (node) => {
        // Match against the full dotted path so "imu.accel" matches the
        // "accel" branch even when typed as a dotted query.
        return fuzzyScore(searchText, node.path) !== null;
      });
    }

    return result;
  }, [tree, groupPrefixes, searchText]);

  // -- Expansion state -------------------------------------------------------

  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());

  const toggle = React.useCallback((path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  // Paths of every branch (node with children) in the *filtered* tree.
  const branchPaths = React.useMemo(() => {
    const paths: string[] = [];
    const walk = (nodes: MetricTreeNode[]) => {
      for (const node of nodes) {
        if (node.children.length > 0) {
          paths.push(node.path);
          walk(node.children);
        }
      }
    };
    walk(filteredTree);
    return paths;
  }, [filteredTree]);

  const expandAll = React.useCallback(
    () => setExpanded(new Set(branchPaths)),
    [branchPaths],
  );

  const collapseAll = React.useCallback(() => setExpanded(new Set()), []);

  const allExpanded =
    branchPaths.length > 0 && branchPaths.every((path) => expanded.has(path));

  // When a search is active, auto-expand every branch in the filtered tree so
  // matching leaves are immediately visible. The expansion state from before
  // the search is saved and restored when the search is cleared.
  const preSearchExpanded = React.useRef<Set<string> | null>(null);

  React.useEffect(() => {
    const isSearching = searchText.trim() !== "";
    if (isSearching) {
      if (preSearchExpanded.current === null) {
        // Capture state on first non-empty search keystroke.
        setExpanded((prev) => {
          preSearchExpanded.current = prev;
          return new Set(branchPaths);
        });
      } else {
        setExpanded(new Set(branchPaths));
      }
    } else if (preSearchExpanded.current !== null) {
      // Restore saved state when search is cleared.
      setExpanded(preSearchExpanded.current);
      preSearchExpanded.current = null;
    }
  }, [searchText, branchPaths]);

  // -- Selection state (URL-driven) ------------------------------------------

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedPath = searchParams.get(SELECTED_METRIC_PARAM);

  const selectedNode = React.useMemo(
    () =>
      selectedPath ? findNodeByPath(filteredTree, selectedPath) : undefined,
    [filteredTree, selectedPath],
  );

  const selectNode = React.useCallback(
    (path: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (next.get(SELECTED_METRIC_PARAM) === path) {
            next.delete(SELECTED_METRIC_PARAM);
          } else {
            next.set(SELECTED_METRIC_PARAM, path);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearSelection = React.useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(SELECTED_METRIC_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // Auto-expand ancestors of a deep-linked selection.
  React.useEffect(() => {
    if (!selectedPath) {
      return;
    }
    const ancestors = ancestorPaths(selectedPath);
    if (ancestors.length === 0) {
      return;
    }
    setExpanded((prev) => {
      if (ancestors.every((path) => prev.has(path))) {
        return prev;
      }
      const next = new Set(prev);
      for (const path of ancestors) {
        next.add(path);
      }
      return next;
    });
  }, [selectedPath]);

  // -- Render ----------------------------------------------------------------

  const hasMetrics = !isLoading && !error && tree.length > 0;

  return (
    <div className={styles.root}>
      {hasMetrics && (
        <div className={styles.toolbar}>
          <TextField
            size="small"
            margin="dense"
            type="search"
            placeholder="Search metrics..."
            aria-label="Search metrics"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className={styles.searchInput}
            InputLabelProps={{ className: styles.inputText }}
            InputProps={{
              className: styles.inputText,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <Autocomplete
            multiple
            size="small"
            autoHighlight
            options={groupOptions}
            value={groupPrefixes}
            onChange={(_event, next) => setGroupPrefixes(next)}
            filterOptions={(opts, state) =>
              state.inputValue === ""
                ? opts
                : fuzzyFilter(state.inputValue, opts)
            }
            className={styles.groupSelect}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                label="Group"
                placeholder={groupPrefixes.length === 0 ? "All groups" : ""}
                InputLabelProps={{
                  ...params.InputLabelProps,
                  className:
                    `${params.InputLabelProps?.className ?? ""} ${styles.inputText}`.trim(),
                }}
                InputProps={{
                  ...params.InputProps,
                  className:
                    `${params.InputProps?.className ?? ""} ${styles.inputText}`.trim(),
                }}
              />
            )}
          />

          {branchPaths.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              className={styles.expandCollapseButton}
              startIcon={
                allExpanded ? (
                  <UnfoldLessIcon fontSize="small" />
                ) : (
                  <UnfoldMoreIcon fontSize="small" />
                )
              }
              onClick={allExpanded ? collapseAll : expandAll}
            >
              {allExpanded ? "Collapse all" : "Expand all"}
            </Button>
          )}
        </div>
      )}

      <div className={styles.layout}>
        <div className={styles.tableArea}>
          {isLoading && (
            <Box className={styles.loading}>
              <CircularProgress size={24} />
            </Box>
          )}
          {error && (
            <Alert severity="error">
              Failed to load metrics for this session: {error.message}
            </Alert>
          )}
          {!isLoading && !error && tree.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No metrics have been published to this session yet.
            </Typography>
          )}
          {hasMetrics && filteredTree.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No metrics match the current filters.
            </Typography>
          )}
          {hasMetrics && filteredTree.length > 0 && (
            <TableContainer>
              <Table size="small" aria-label="session metrics">
                <TableHead>
                  <TableRow>
                    <TableCell>Metric</TableCell>
                    <TableCell align="right">Records</TableCell>
                    <TableCell align="right">Latest value</TableCell>
                    <TableCell>Last published</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTree.flatMap((root) =>
                    renderRows(
                      root,
                      expanded,
                      toggle,
                      selectedPath,
                      selectNode,
                      searchText,
                    ),
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>

        {selectedNode && (
          <div className={styles.detailPane}>
            <MetricDetailCard
              node={selectedNode}
              orgId={orgId}
              onClose={clearSelection}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** Small magnifying-glass icon for the search adornment, avoids pulling in a
 *  full icon package for a single glyph. Matches the 20×20 sizing MUI
 *  `InputAdornment` expects for "small" TextFields. */
function SearchIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function renderRows(
  node: MetricTreeNode,
  expanded: Set<string>,
  toggle: (path: string) => void,
  selectedPath: string | null,
  onSelect: (path: string) => void,
  searchText: string,
): React.ReactElement[] {
  const rows: React.ReactElement[] = [
    <NodeRow
      key={node.path}
      node={node}
      expanded={expanded.has(node.path)}
      selected={node.path === selectedPath}
      toggle={toggle}
      onSelect={onSelect}
      searchText={searchText}
    />,
  ];
  if (expanded.has(node.path)) {
    for (const child of node.children) {
      rows.push(
        ...renderRows(
          child,
          expanded,
          toggle,
          selectedPath,
          onSelect,
          searchText,
        ),
      );
    }
  }
  return rows;
}

interface NodeRowProps {
  node: MetricTreeNode;
  expanded: boolean;
  selected: boolean;
  toggle: (path: string) => void;
  onSelect: (path: string) => void;
  searchText: string;
}

function NodeRow({
  node,
  expanded,
  selected,
  toggle,
  onSelect,
  searchText,
}: NodeRowProps) {
  const isBranch = node.children.length > 0;
  const isSelectable = node.records.length > 0;
  const latest = latestRecord(node.records);

  const handleRowClick = () => {
    if (isSelectable) {
      onSelect(node.path);
    } else if (isBranch) {
      toggle(node.path);
    }
  };

  const className = [
    styles.nodeRow,
    isBranch ? null : styles.leafRow,
    isSelectable || isBranch ? styles.clickableRow : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <TableRow
      hover
      selected={selected}
      onClick={handleRowClick}
      className={className}
    >
      <TableCell>
        <Box
          className={styles.nameCell}
          style={{ paddingLeft: `calc(${node.depth} * var(--spacing-md))` }}
        >
          {isBranch ? (
            <IconButton
              size="small"
              aria-label={expanded ? "Collapse" : "Expand"}
              aria-expanded={expanded}
              onClick={(event) => {
                event.stopPropagation();
                toggle(node.path);
              }}
              className={styles.chevron}
            >
              {expanded ? (
                <KeyboardArrowDownIcon fontSize="small" />
              ) : (
                <KeyboardArrowRightIcon fontSize="small" />
              )}
            </IconButton>
          ) : (
            <span className={styles.chevronSpacer} aria-hidden="true" />
          )}
          <Box className={styles.labels}>
            <span>
              <FuzzyHighlight
                text={node.path}
                query={searchText}
                partial
                slice={[node.path.length - node.label.length, node.path.length]}
              />
            </span>
            {node.records.length > 0 && (
              <code className={styles.path}>
                <FuzzyHighlight text={node.path} query={searchText} partial />
              </code>
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell align="right">
        {isBranch ? node.recordCount : EM_DASH}
      </TableCell>
      <TableCell align="right">{latest ? latest.value : EM_DASH}</TableCell>
      <TableCell>
        {latest ? new Date(latest.published).toLocaleString() : EM_DASH}
      </TableCell>
    </TableRow>
  );
}

interface FuzzyHighlightProps {
  /** Full text to match the query against. */
  text: string;
  /** Fuzzy query string. */
  query: string;
  /** When true, use partial matching — highlights however many query characters
   *  can be consumed by `text` even when `text` is too short for the full query.
   *  Useful for nodes whose path only covers a prefix of the query. */
  partial?: boolean;
  /** Optional `[start, end)` range — only characters within this slice of
   *  `text` are rendered, but matching runs against the full `text`. Useful
   *  for rendering just the label segment of a dotted path. */
  slice?: [number, number];
}

/**
 * Renders `text` (or a slice of it) with the characters matched by `query`
 * wrapped in a `<mark>`. When `query` is empty or produces no match indices
 * the plain text is returned.
 */
function FuzzyHighlight({ text, query, partial, slice }: FuzzyHighlightProps) {
  const indices = React.useMemo(() => {
    if (query.trim() !== "") {
      return partial
        ? fuzzyMatchIndicesPartial(query, text)
        : fuzzyMatchIndices(query, text);
    } else {
      return null;
    }
  }, [query, text, partial]);

  const start = slice ? slice[0] : 0;
  const end = slice ? slice[1] : text.length;
  const display = text.slice(start, end);

  if (!indices || indices.length === 0) {
    return <>{display}</>;
  }

  const matchSet = new Set(indices);
  const parts: React.ReactNode[] = [];
  let run = "";
  let inMatch = false;

  for (let i = start; i < end; i++) {
    const isMatch = matchSet.has(i);
    if (isMatch !== inMatch && run.length > 0) {
      parts.push(
        inMatch ? (
          <mark key={parts.length} className={styles.highlight}>
            {run}
          </mark>
        ) : (
          <span key={parts.length}>{run}</span>
        ),
      );
      run = "";
    }
    run += text[i];
    inMatch = isMatch;
  }

  if (run.length > 0) {
    parts.push(
      inMatch ? (
        <mark key={parts.length} className={styles.highlight}>
          {run}
        </mark>
      ) : (
        <span key={parts.length}>{run}</span>
      ),
    );
  }

  return <>{parts}</>;
}
